/**
 * Universal Safe Area CSS Values
 * 
 * This module provides platform-aware safe area values that work across:
 * - iOS: Uses env(safe-area-inset-*) via CSS variables set in index.css
 * - Android: Uses CSS variables (--sat, --sab, etc.) injected by MainActivity.java
 * - Web: Falls back to 0px
 * 
 * CSS Variables Used:
 * --sat = Safe Area Top
 * --sab = Safe Area Bottom
 * --sal = Safe Area Left
 * --sar = Safe Area Right
 * 
 * These are set by:
 * - iOS: @supports rule in index.css maps env() to --sat/--sab/etc.
 * - Android: MainActivity.java injects actual device values
 */

/**
 * Safe area CSS values for use in inline styles.
 * Uses var() to reference the CSS custom properties.
 */
export const SAFE_AREA = {
  top: "var(--sat, 0px)",
  bottom: "var(--sab, 0px)",
  left: "var(--sal, 0px)",
  right: "var(--sar, 0px)",
} as const;

/**
 * Get safe area value for a specific direction.
 * Returns a CSS var() expression that resolves to the actual safe area value.
 */
export function getSafeArea(direction: 'top' | 'bottom' | 'left' | 'right'): string {
  return SAFE_AREA[direction];
}

/**
 * Create a calc() expression combining safe area with additional value.
 * 
 * @example
 * calcWithSafeArea('top', '56px') // Returns "calc(var(--sat, 0px) + 56px)"
 */
export function calcWithSafeArea(direction: 'top' | 'bottom' | 'left' | 'right', additional: string): string {
  return `calc(${SAFE_AREA[direction]} + ${additional})`;
}

/**
 * Get computed safe area value in pixels.
 * Useful when you need the actual numeric value for calculations.
 * Returns 0 if running server-side or CSS variables not yet loaded.
 */
export function getSafeAreaPixels(direction: 'top' | 'bottom' | 'left' | 'right'): number {
  if (typeof document === 'undefined') return 0;
  
  const varNames = { top: '--sat', bottom: '--sab', left: '--sal', right: '--sar' };
  const value = getComputedStyle(document.documentElement).getPropertyValue(varNames[direction]);
  return parseInt(value, 10) || 0;
}
